import { LibraryPageContext } from "@/pages/library_v2/components/filesView/context";
import {
  FileSortKey,
  SORT_ARROW,
  SortDirection,
} from "@/pages/library_v2/components/filesView/types";
import { getShowFolderColumn } from "@/pages/library_v2/components/filesView/utils";
import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo } from "react";

import { twMerge } from "tailwind-merge";

export const TABLE_GRID_WITH_FOLDER =
  "grid grid-cols-[86px_minmax(0,1fr)_168px_108px_104px_136px] items-center gap-x-3";
export const TABLE_GRID_NO_FOLDER =
  "grid grid-cols-[86px_minmax(0,1fr)_108px_104px_136px] items-center gap-x-3";

export const TABLE_ROW_HEIGHT_PX = 70;
export const TABLE_VISIBLE_ROWS = 10;

export const FileListTableHeaders: React.FC = () => {
  const { t } = useTrans();
  const {
    selectedFolderName,
    selectedViewId,
    searchValue,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection,
  } = useContext(LibraryPageContext);

  const showFolderColumn = useMemo(
    () =>
      getShowFolderColumn({ selectedFolderName, selectedViewId, searchValue }),
    [selectedFolderName, selectedViewId, searchValue],
  );

  const onSort = useCallback(
    (key: FileSortKey) => {
      if (key === sortKey) {
        setSortDirection(
          sortDirection === SortDirection.ASC
            ? SortDirection.DESC
            : SortDirection.ASC,
        );
      } else {
        setSortDirection(SortDirection.ASC);
      }

      setSortKey(key);
    },
    [setSortDirection, setSortKey, sortDirection, sortKey],
  );

  const sortableHeader = useCallback(
    (key: FileSortKey, label: string) => {
      const active = sortKey === key;
      return (
        <button
          type="button"
          onClick={() => onSort(key)}
          className="inline-flex cursor-pointer items-center gap-[5px] hover:text-[#1D1C1C]"
        >
          {label}
          <span className={active ? "text-[#1D1C1C]" : "text-[#c4bcbc]"}>
            {active ? SORT_ARROW[sortDirection] : "↕"}
          </span>
        </button>
      );
    },
    [onSort, sortDirection, sortKey],
  );

  return (
    <div
      className={twMerge(
        showFolderColumn ? TABLE_GRID_WITH_FOLDER : TABLE_GRID_NO_FOLDER,
        "border-grey2 text-grey6 border-t border-b bg-[#FBF8F8] px-5 py-[10px] text-[11.5px] font-bold tracking-[0.03em]",
      )}
    >
      <div />
      <div>
        {sortableHeader(
          FileSortKey.NAME,
          t("libraryPage:fileListTableFileName"),
        )}
      </div>
      {showFolderColumn && (
        <div>
          {sortableHeader(
            FileSortKey.FOLDER,
            t("libraryPage:fileListTableFolder"),
          )}
        </div>
      )}
      <div>
        {sortableHeader(
          FileSortKey.DATE,
          t("libraryPage:fileListTableDateUploaded"),
        )}
      </div>
      <div>{t("libraryPage:fileListTableTenantView")}</div>
      <div className="text-right">{t("libraryPage:fileListTableActions")}</div>
    </div>
  );
};
